/**
 * Working memory tab — shows the agent's plan, findings, gaps, and indicators.
 */

import {
  AlertTriangle,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleDot,
  Clock,
  Loader2,
  TrendingDown,
} from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/composed/sections/collapsible";
import { cn } from "@/utils/misc";
import { SeverityBadge } from "./StatusBadges";

// ---------------------------------------------------------------------------
// Working memory shape
//
// Working memory is stored as `v.any()` on `summarizationTasks.workingMemory`
// (and returned as `v.any()` from `getSummarizationTaskDetail`) — it is
// agent-maintained state with no backend-enforced schema. These types
// document the fields this view knows how to render. The boundary is
// `unknown`, narrowed by the shallow `isWorkingMemory` guard; individual
// values are still rendered defensively.
// ---------------------------------------------------------------------------

interface PlanItem {
  id?: string | number;
  status?: string;
  description?: string;
  notes?: string;
  priority?: string;
}

interface MemoryFinding {
  description?: string;
  category?: string;
  source?: string;
  period?: string;
  significance?: string;
  metrics?: unknown[];
}

interface InsolvencyIndicator {
  severity: string;
  indicator?: string;
  firstSeen?: string;
  evidence?: string;
}

interface DataGap {
  description?: string;
  impact?: string;
  recommendation?: string;
}

interface WorkingMemory {
  plan?: PlanItem[];
  findings?: MemoryFinding[];
  gaps?: DataGap[];
  insolvencyIndicators?: InsolvencyIndicator[];
}

/**
 * Shallow structural guard: any plain object may carry the (all-optional)
 * fields above. Leaf values are still guarded where the JSX renders them.
 */
function isWorkingMemory(value: unknown): value is WorkingMemory {
  return typeof value === "object" && value !== null;
}

export function WorkingMemoryView({
  memory,
  isRunning,
}: {
  memory: unknown;
  isRunning: boolean;
}) {
  const parsed: WorkingMemory = isWorkingMemory(memory) ? memory : {};
  const plan = parsed.plan ?? [];
  const findings = parsed.findings ?? [];
  const gaps = parsed.gaps ?? [];
  const indicators = parsed.insolvencyIndicators ?? [];

  const done = plan.filter((i) => i.status === "done").length;
  const pending = plan.filter((i) => i.status === "pending").length;
  const inProgress = plan.filter((i) => i.status === "in_progress").length;

  return (
    <div className="flex flex-col gap-6 pb-4 pr-4">
      {/* Plan / Todo List */}
      {plan.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <CircleDot className="h-4 w-4 text-primary/70" />
            <h4 className="text-sm font-semibold">Analysis Plan</h4>
            <span className="ml-auto text-xs text-muted-foreground">
              {done}/{plan.length} complete
              {pending > 0 && ` · ${pending} pending`}
              {inProgress > 0 && ` · ${inProgress} in progress`}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            {plan.map((item, i) => (
              <div
                key={item.id ?? i}
                className={cn(
                  "flex items-start gap-2.5 rounded-md px-3 py-2 text-sm",
                  item.status === "done" &&
                    "bg-emerald-50/50 dark:bg-emerald-950/10",
                  item.status === "in_progress" &&
                    "bg-blue-50/50 dark:bg-blue-950/20",
                  item.status === "skipped" && "opacity-50",
                )}
              >
                {item.status === "done" && (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                )}
                {item.status === "pending" && (
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                {item.status === "in_progress" && (
                  <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-blue-500" />
                )}
                {item.status === "skipped" && (
                  <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <span
                    className={cn(
                      (item.status === "done" || item.status === "skipped") &&
                        "text-muted-foreground line-through",
                    )}
                  >
                    {item.description}
                  </span>
                  {item.notes && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {item.notes}
                    </p>
                  )}
                </div>
                {item.priority && (
                  <Badge
                    variant={
                      item.priority === "high" ? "destructive" : "outline"
                    }
                    className="shrink-0 text-[10px]"
                  >
                    {item.priority}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Findings summary */}
      {findings.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary/70" />
            <h4 className="text-sm font-semibold">
              Findings ({findings.length})
            </h4>
          </div>
          <div className="flex flex-col gap-1.5">
            {findings.map((f, i) => (
              <div
                key={i}
                className="flex items-start gap-2.5 rounded-md border px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="leading-relaxed">{f.description}</p>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge variant="outline" size="sm">
                      {f.category}
                    </Badge>
                    {f.source && (
                      <span className="text-xs text-muted-foreground">
                        {f.source}
                      </span>
                    )}
                    {f.period && (
                      <span className="text-xs text-muted-foreground">
                        {f.period}
                      </span>
                    )}
                    {f.metrics && f.metrics.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {f.metrics.length} data points
                      </span>
                    )}
                  </div>
                </div>
                {f.significance && <SeverityBadge severity={f.significance} />}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Insolvency Indicators */}
      {indicators.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-destructive/70" />
            <h4 className="text-sm font-semibold">
              Insolvency Indicators ({indicators.length})
            </h4>
          </div>
          <div className="flex flex-col gap-2">
            {indicators.map((ind, i) => (
              <div
                key={i}
                className="rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2"
              >
                <div className="mb-1 flex items-center gap-2">
                  <SeverityBadge severity={ind.severity} />
                  <span className="text-sm font-medium">{ind.indicator}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  <span>First seen: {ind.firstSeen}</span>
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {ind.evidence}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Data Gaps */}
      {gaps.length > 0 && (
        <div>
          <div className="mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500/70" />
            <h4 className="text-sm font-semibold">Data Gaps ({gaps.length})</h4>
          </div>
          <div className="flex flex-col gap-2">
            {gaps.map((gap, i) => (
              <div
                key={i}
                className="rounded-md border border-amber-500/20 bg-amber-50/30 px-3 py-2 text-sm dark:bg-amber-950/10"
              >
                <p className="font-medium">{gap.description}</p>
                {gap.impact && (
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    Impact: {gap.impact}
                  </p>
                )}
                {gap.recommendation && (
                  <p className="mt-0.5 text-xs text-primary/80">
                    Recommendation: {gap.recommendation}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {plan.length === 0 && findings.length === 0 && (
        <div className="py-8 text-center text-sm text-muted-foreground">
          {isRunning ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
              <span>Agent is building its working memory...</span>
            </div>
          ) : (
            "Working memory is empty."
          )}
        </div>
      )}

      {/* Raw memory for debugging */}
      <Collapsible>
        <CollapsibleTrigger className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ChevronDown className="h-3 w-3" />
          Raw working memory
        </CollapsibleTrigger>
        <CollapsibleContent>
          <pre className="mt-2 max-h-[50vh] overflow-auto rounded-md bg-muted/30 p-3 text-[11px] leading-relaxed">
            {JSON.stringify(memory, null, 2)}
          </pre>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
